import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";
import { calculateAffineParams, calculateResiduals, type GCP, type Residual } from "~/features/georeferencer/utils";
import { cn } from "~/lib/utils";

interface GCPTableProps {
  gcps: GCP[];
  onClearAll: () => void;
  onRemoveGcp: (id: string) => void;
  selectedGcpId?: string | null;
  onGcpSelect?: (id: string) => void;
}

export function GCPTable({ gcps, onClearAll, onRemoveGcp, selectedGcpId = null, onGcpSelect }: GCPTableProps) {
  const { t } = useTranslation();

  const { affineParams, residuals } = useMemo(() => {
    const params = calculateAffineParams(gcps);
    const residualMap = params ? calculateResiduals(gcps, params) : new Map<string, Residual>();
    return { affineParams: params, residuals: residualMap };
  }, [gcps]);

  const residualText = (id: string, pick: (r: Residual) => number): string => {
    const r = affineParams ? residuals.get(id) : undefined;
    return r ? pick(r).toFixed(2) : "—";
  };

  return (
    <div className="rounded-[var(--radius-md)] border border-border-default bg-bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-16-bold uppercase tracking-[0.04em] text-fg-1">{t("ui.gcps")}</h2>
        <Button type="button" variant="outline" size="sm" onClick={onClearAll} disabled={gcps.length === 0}>
          {t("ui.clearAllX", { x: t("ui.gcps") })}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-[13px]">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("ui.sourceX", { x: "X" })}</Th>
              <Th>{t("ui.sourceX", { x: "Y" })}</Th>
              <Th>{t("ui.targetX", { x: "X" })}</Th>
              <Th>{t("ui.targetX", { x: "Y" })}</Th>
              <Th>{t("ui.dX")}</Th>
              <Th>{t("ui.dY")}</Th>
              <Th>{t("ui.residual")}</Th>
              <Th className="text-right">{t("ui.actions")}</Th>
            </tr>
          </thead>
          <tbody>
            {gcps.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3.5 py-6 text-center text-fg-3">
                  {t("ui.noData")}
                </td>
              </tr>
            ) : (
              gcps.map((gcp) => (
                <tr
                  key={gcp.id}
                  onClick={() => onGcpSelect?.(gcp.id)}
                  className={cn(
                    "cursor-pointer border-b border-border-default last:border-b-0 hover:bg-bg-subtle",
                    gcp.id === selectedGcpId && "bg-bg-tint",
                  )}
                >
                  <Td>{gcp.sourceX.toFixed(2)}</Td>
                  <Td>{gcp.sourceY.toFixed(2)}</Td>
                  <Td>{gcp.targetX.toFixed(6)}</Td>
                  <Td>{gcp.targetY.toFixed(6)}</Td>
                  <Td>{residualText(gcp.id, (r) => r.dX)}</Td>
                  <Td>{residualText(gcp.id, (r) => r.dY)}</Td>
                  <Td>{residualText(gcp.id, (r) => Math.sqrt(r.dX ** 2 + r.dY ** 2))}</Td>
                  <td className="px-3.5 py-2 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveGcp(gcp.id);
                      }}
                    >
                      {t("ui.removeX", { x: t("ui.gcp") })}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "whitespace-nowrap border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap px-3.5 py-2 tabular-nums text-fg-2">{children}</td>;
}
